// src/components/Theme/ThemeTime.js

import React, { useRef, useState, useEffect } from 'react';
import { Group, Text, Line as KonvaLine, Rect as KonvaRect } from 'react-konva';
import PropTypes from 'prop-types';
import { NumberRenderer, SPECIAL_CHARACTERS } from '../DigitRenderer';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';


// ThemeTimeSettings Component
export const ThemeTimeSettings = ({ element, updateElement, openColorModal }) => {
  // Handler for size change
  const handleSizeChange = (e) => {
    const size = parseInt(e.target.value, 10);
    if (!isNaN(size) && size > 0) {
      updateElement(element.id, 'size', size);
    }
  };

  // Handlers for X and Y position changes
  const handleXChange = (e) => {
    const newX = parseInt(e.target.value, 10);
    if (!isNaN(newX)) {
      updateElement(element.id, 'x', newX);
    }
  };

  const handleYChange = (e) => {
    const newY = parseInt(e.target.value, 10);
    if (!isNaN(newY)) {
      updateElement(element.id, 'y', newY);
    }
  };

  // Handler for usage change
  const handleUsageChange = (e) => {
    const usage = parseInt(e.target.value, 10);
    updateElement(element.id, 'usage', usage);
  };

  return (
    <div>
      <label>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
        />
      </label>
      <label>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
        />
      </label>
      <label>
        Font Size:
        <input
          className={styles.inputNumber}
          type="number"
          min="5"
          value={element.size}
          onChange={handleSizeChange}
        />
      </label>
      <label>
        Usage:
        <select
          className={styles.select200}
          value={element.usage || 0} // Default to 0 (time)
          onChange={handleUsageChange}
        >
          <option value={0}>Time</option>
          <option value={1}>Running Time</option>
          <option value={2}>Navigation ETA</option>
        </select>
      </label>

      {/* Color */}
      <CustomColorPicker
        label="Color"
        color={element.color}
        onChange={(value) => { updateElement(element.id, 'color', value); }}
      />
    </div>
  );
};

ThemeTimeSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired, // Font size
    color: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired, // Time text (e.g., "12:34")
    usage: PropTypes.number.isRequired, // Usage (0 = Time, 1 = Running Time)
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
  openColorModal: PropTypes.func.isRequired,
};


/////////////////////////////////////
const ThemeTime = ({
  element,
  selectedElement,
  setSelectedElement,
  updateElement,
  render,editAllowed,
  scale,
  snapToGridEnabled,
  gridSize
}) => {
  const groupRef = useRef();
  const [displayValue, setDisplayValue] = useState('');

  const spacing = element.size / 2;

  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();
    updateElement(element.id, 'x', Math.round(x + getTotalWidth()));
    updateElement(element.id, 'y', Math.round(y));
  };

  // Function to generate the display text based on usage
  const getDisplayValue = () => {
    if (element.usage === 1) {
      // Running Time: HH:MM:SS
      return '9:59:59';
    }
    // Default Time: HH:MM
    return '23:59';
  };

  // Calculate total width for spacing
  const getTotalWidth = () => {
    let totalWidth = 0;
    const chars = displayValue.split('');
    chars.forEach((ch) => {
      if (ch >= '0' && ch <= '9') {
        totalWidth += element.size * 3 + spacing;
      } else if (SPECIAL_CHARACTERS[ch]) {
        let width = 0;
        if (ch === '.' || ch === ':') {
          width = element.size;
        } else if (ch === '-') {
          width = element.size * 2;
        } else if (ch === ' ') {
          width = element.size * 3;
        } else if (ch === '/') {
          width = element.size * 3;
        } else {
          width = element.size * 3; // Default width
        }
        totalWidth += width + spacing;
      }
    });
    return totalWidth;
  };

  const totalWidth = getTotalWidth();

  const isSelected = selectedElement && selectedElement.id === element.id;

  // Recalculate the display value whenever `element.usage` or `element.text` changes
  useEffect(() => {
    setDisplayValue(getDisplayValue());
  }, [element.usage, element.text]);

  return (
    <Group
      x={element.x - totalWidth}
      y={element.y}
      draggable={!render && editAllowed}
      onDragEnd={handleDragEnd}
      dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, element.size, element.size) }}
      onClick={() => setSelectedElement(element)}
      ref={groupRef}
    >
      <NumberRenderer
        number={displayValue}
        x={totalWidth}
        y={0}
        size={element.size}
        color={element.color}
        spacing={spacing}
      />
      {!render && (
        <KonvaRect
          x={0}
          y={0}
          width={totalWidth}
          height={element.size * 5}
          stroke="#1976d2"
          strokeWidth={0.5}
          dash={isSelected ? [1, 1] : [0, 0]}
        />
      )}
    </Group>
  );
};

ThemeTime.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string, // Time or Running Time text
    size: PropTypes.number.isRequired, // Font size
    color: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    usage: PropTypes.number.isRequired, // 0 for Time, 1 for Running Time
  }).isRequired,
  selectedElement: PropTypes.object,
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  render: PropTypes.bool.isRequired,
  editAllowed: PropTypes.bool,
};

export default ThemeTime;